import { useQuery } from '@tanstack/react-query'

import type { ApiError } from '@mangovoice/types'

import { useExtensionsApi } from '../api/context'
import { EXTENSION_QUERY_KEYS } from '../api/endpoints'
import type {
  CallerIdOption,
  LocationOption,
  MobileDevice,
  PlaylistOption,
  UserGroupOption,
  VoipProfile,
} from '../types/api.types'

/**
 * Hook to search locations for a PBX (Update Location modal ajax search).
 */
export const useLocationsSearch = (
  pbxId: number | undefined | null,
  search: string,
) => {
  const api = useExtensionsApi()

  const query = useQuery<LocationOption[], ApiError>({
    queryKey: ['locations', pbxId, search],
    queryFn: async () => {
      if (pbxId == null || Number.isNaN(pbxId)) {
        return []
      }
      const { ok, data } = await api.getLocations({
        pbx_id: pbxId,
        search: search.trim() || undefined,
      })
      if (!ok || !data) {
        return Promise.reject(
          (data as ApiError) ?? new Error('Failed to fetch locations'),
        )
      }
      return (data as { results: LocationOption[] }).results ?? []
    },
    enabled: pbxId != null && !Number.isNaN(pbxId),
  })

  return {
    locations: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as ApiError | null,
  }
}

/**
 * Hook to load PBX system defaults used by line edit/create screens.
 */
export const usePbxSystemDefaults = (pbxId: number | undefined | null) => {
  const api = useExtensionsApi()

  const query = useQuery<
    {
      default_area_code?: string
      default_caller_id?: string
      default_time_zone?: string
    } | null,
    ApiError
  >({
    queryKey: ['systemDefaults', pbxId],
    queryFn: async () => {
      if (pbxId == null || Number.isNaN(pbxId)) {
        return null
      }
      const { ok, data } = await api.getSystemDefaults({ pbx_id: pbxId })
      if (!ok || !data) {
        return Promise.reject(
          (data as ApiError) ?? new Error('Failed to fetch system defaults'),
        )
      }
      return data
    },
    enabled: pbxId != null && !Number.isNaN(pbxId),
  })

  return {
    systemDefaults: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error as ApiError | null,
  }
}

/**
 * Mirrors the backend's `unformat_phone_number` so that values stored via
 * the frontend match the format legacy-admin writes.
 *
 * US/CA (+1): "+15551234567" → "5551234567"  (10-digit national, matches
 *             how `phone_numbers.number` is stored for US deployments)
 * Non-US:    "+442079460015" → "+442079460015"  (E.164 unchanged, matches
 *             how `phone_numbers.number` is stored for non-US deployments)
 */
function toNationalNumber(value: string): string {
  if (!value) return value
  const digits = value.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1)
  }
  return value
}

/**
 * Hook to load caller ID options for a PBX.
 */
export const usePbxCallerIdOptions = (pbxId: number | undefined | null) => {
  const api = useExtensionsApi()

  const query = useQuery<CallerIdOption[], ApiError>({
    queryKey: ['callerIds', pbxId],
    queryFn: async () => {
      if (pbxId == null || Number.isNaN(pbxId)) {
        return []
      }
      const { ok, data } = await api.getPhoneNumbers({
        pbx_id: pbxId,
      })
      if (!ok || !data) {
        return Promise.reject(
          (data as ApiError) ?? new Error('Failed to fetch caller ID options'),
        )
      }
      const rows = (
        data as {
          results?: {
            name?: string
            number?: string
          }[]
        }
      ).results
      return (rows ?? [])
        .filter((row) => Boolean(row.number))
        .map((row) => ({
          value: toNationalNumber(row.number as string),
          label: `${row.name || 'Number'} - ${row.number as string}`,
        }))
    },
    enabled: pbxId != null && !Number.isNaN(pbxId),
  })

  return {
    callerIdOptions: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as ApiError | null,
  }
}

/**
 * Hook to fetch playlists for a PBX.
 */
export const usePbxPlaylists = (pbxId: number | undefined | null) => {
  const api = useExtensionsApi()

  const query = useQuery<PlaylistOption[], ApiError>({
    queryKey: ['playlists', pbxId],
    queryFn: async () => {
      if (pbxId == null || Number.isNaN(pbxId)) {
        return []
      }
      const { ok, data } = await api.getPlaylists({ pbx_id: pbxId })
      if (!ok || !data) {
        return Promise.reject(
          (data as ApiError) ?? new Error('Failed to fetch playlists'),
        )
      }
      return Array.isArray(data) ? data : []
    },
    enabled: pbxId != null && !Number.isNaN(pbxId),
  })

  return {
    playlists: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as ApiError | null,
  }
}

/**
 * Hook to fetch PBX sound clips.
 */
export const usePbxSoundClips = (pbxId: number | undefined | null) => {
  const api = useExtensionsApi()

  const query = useQuery<{ id: number; name: string }[], ApiError>({
    queryKey: ['soundClips', pbxId],
    queryFn: async () => {
      if (pbxId == null || Number.isNaN(pbxId)) {
        return []
      }
      // The endpoint paginates at 25 by default; a high limit pulls the full
      // list so the greeting dropdown isn't silently truncated (SoundClipViewSet
      // has no paginate=false override, so limit is the only lever).
      const { ok, data } = await api.getSoundClips({
        pbx_id: pbxId,
        limit: 1000,
      })
      if (!ok || !data) {
        return Promise.reject(
          (data as ApiError) ?? new Error('Failed to fetch sound clips'),
        )
      }
      return (
        (data as { results?: { id: number; name: string }[] }).results ?? []
      )
    },
    enabled: pbxId != null && !Number.isNaN(pbxId),
  })

  return {
    soundClips: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as ApiError | null,
  }
}

/**
 * Hook to search user groups for a PBX (virtual fax / shared voicemail picklist).
 */
export const usePbxUserGroups = (
  pbxId: number | undefined | null,
  search: string,
) => {
  const api = useExtensionsApi()

  const query = useQuery<UserGroupOption[], ApiError>({
    queryKey: ['userGroups', pbxId, search],
    queryFn: async () => {
      if (pbxId == null || Number.isNaN(pbxId)) {
        return []
      }
      // The endpoint paginates at 25 by default, so without a high limit the
      // Available user groups picklist silently drops every group past the
      // first page (worse after the client-side is_message_group filter below).
      // The viewset has no paginate=false override, so limit is the only lever.
      const { ok, data } = await api.getPbxUserGroups({
        pbx_id: pbxId,
        search: search.trim() || undefined,
        limit: 1000,
      })
      if (!ok || !data) {
        return Promise.reject(
          (data as ApiError) ?? new Error('Failed to fetch user groups'),
        )
      }
      return (data.results ?? []).filter((group) => !group.is_message_group)
    },
    enabled: pbxId != null && !Number.isNaN(pbxId),
  })

  return {
    userGroups: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as ApiError | null,
  }
}

/**
 * Query to fetch mobile devices for the assigned user of an extension.
 */
export const useMobileDevices = (
  extensionId: number | string | undefined | null,
) => {
  const api = useExtensionsApi()

  return useQuery<MobileDevice[]>({
    queryKey: [
      ...EXTENSION_QUERY_KEYS.detail(extensionId ?? 0),
      'mobile-devices',
    ],
    queryFn: async () => {
      if (extensionId == null) return []
      const { ok, data } = await api.getMobileDevices(extensionId)
      if (!ok) return []
      return data ?? []
    },
    enabled: extensionId != null,
  })
}

/**
 * Hook to fetch users for a PBX (for assigning users to extensions).
 */
export const useGetUsers = (pbxId: number) => {
  const api = useExtensionsApi()

  const query = useQuery<{ id: number; full_name?: string }[]>({
    queryKey: ['extensionUsers', pbxId],
    queryFn: async () => {
      if (!pbxId || pbxId <= 0) return []
      const { ok, data } = await api.getUsers({ pbx_id: pbxId })
      if (!ok || !data) return []
      return data
    },
    enabled: pbxId > 0,
    retry: false,
  })

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  }
}

/**
 * Hook to fetch VoIP profiles for a PBX (for assigning profiles to line extensions).
 */
export const useVoipProfiles = (
  pbxId: number | undefined | null,
  subjectType = 'user',
) => {
  const api = useExtensionsApi()

  const query = useQuery<VoipProfile[], ApiError>({
    queryKey: ['voipProfiles', pbxId, subjectType],
    queryFn: async () => {
      if (pbxId == null || Number.isNaN(pbxId) || pbxId <= 0) return []
      const { ok, data } = await api.getVoipProfiles({
        pbx_id: pbxId,
        subject_type: subjectType,
      })
      if (!ok || !data) return []
      return data
    },
    enabled: pbxId != null && !Number.isNaN(pbxId) && pbxId > 0,
  })

  return {
    profiles: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as ApiError | null,
  }
}
